import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Common from "../../Common/Common";
import "./Modal.css";
import "./RecalculateDialog.css";

const PACE_OPTIONS = Common.PACE_OPTIONS;

const RecalculateDialog = ({ show, onHide, onConfirm, title, defaultStart, defaultEnd }) => {
  const [startHour, setStartHour] = useState(defaultStart ?? 9);
  const [endHour, setEndHour] = useState(defaultEnd ?? 20);
  // Defaults to preserving each event's own existing schedule duration
  // rather than Place.Duration — a field many users never touch, and one
  // that (unlike a schedule-set duration, which is lost for good once
  // overwritten) can always be pulled in later by explicitly switching this.
  const [usePlaceDuration, setUsePlaceDuration] = useState(false);
  const [avoidClosed, setAvoidClosed] = useState(true);
  // Defaults to Normal (1, a no-op) rather than Build My Trip's Relaxed
  // default — this re-times an EXISTING schedule the user already built, so
  // the base action should leave each event's duration untouched unless the
  // user deliberately asks to stretch/compress it. Lets a user switch pace
  // without re-searching for places (was previously only settable when
  // building a new schedule from scratch). Found 2026-09-30.
  const [paceMultiplier, setPaceMultiplier] = useState("1");
  // "Recalculate Times" and "Optimize by Location" used to be two separate
  // menu items/dialogs for what is internally the same function with a
  // single boolean flag (Schedule.js's `geography` param) — merged into one
  // dialog with this toggle, defaulting off so the base action stays
  // "recalculate times without reordering" and reordering is opt-in.
  const [reorderByLocation, setReorderByLocation] = useState(false);

  useEffect(() => {
    if (show) {
      setStartHour(defaultStart ?? 9);
      setEndHour(defaultEnd ?? 20);
      setAvoidClosed(true);
      setReorderByLocation(false);
      setPaceMultiplier("1");
    }
  }, [show, defaultStart, defaultEnd]);

  const allHours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={onHide}
        animation={false}
        dialogClassName="rcd-dialog"
        aria-labelledby="rcd-title"
        centered
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="rcd-shell">

            <div className="rcd-header">
              <div className="rcd-header-icon">
                <FontAwesomeIcon icon={solid("clock-rotate-left")} />
              </div>
              <h2 className="rcd-title" id="rcd-title">{title ?? "Recalculate Schedule"}</h2>
              <button className="rcd-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="rcd-body">

              <div className="rcd-row">
                <span className="rcd-label">Day start</span>
                <select
                  className="rcd-hour-select"
                  value={startHour}
                  onChange={(e) => {
                    const h = parseInt(e.target.value);
                    setStartHour(h);
                    if (endHour <= h) setEndHour(h + 1);
                  }}
                >
                  {allHours.map(h => <option key={h} value={h}>{h}:00</option>)}
                </select>
              </div>

              <div className="rcd-row">
                <span className="rcd-label">Day end</span>
                <select
                  className="rcd-hour-select"
                  value={endHour}
                  onChange={(e) => {
                    const h = parseInt(e.target.value);
                    if (h > startHour) setEndHour(h);
                  }}
                >
                  {allHours.filter(h => h > startHour).map(h => <option key={h} value={h}>{h}:00</option>)}
                </select>
              </div>

              <div className="rcd-row">
                <span className="rcd-label">Reorder by location</span>
                <div className="rcd-seg">
                  <button
                    className={`rcd-seg-btn${reorderByLocation ? " active" : ""}`}
                    onClick={() => setReorderByLocation(true)}
                  >
                    On
                  </button>
                  <button
                    className={`rcd-seg-btn${!reorderByLocation ? " active" : ""}`}
                    onClick={() => setReorderByLocation(false)}
                  >
                    Off
                  </button>
                </div>
              </div>

              {reorderByLocation ? (
                <div className="rcd-row">
                  <span className="rcd-label">Avoid closed places</span>
                  <div className="rcd-seg">
                    <button
                      className={`rcd-seg-btn${avoidClosed ? " active" : ""}`}
                      onClick={() => setAvoidClosed(true)}
                    >
                      On
                    </button>
                    <button
                      className={`rcd-seg-btn${!avoidClosed ? " active" : ""}`}
                      onClick={() => setAvoidClosed(false)}
                    >
                      Off
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="rcd-row">
                <span className="rcd-label">Duration</span>
                <div className="rcd-seg">
                  <button
                    className={`rcd-seg-btn${usePlaceDuration ? " active" : ""}`}
                    onClick={() => setUsePlaceDuration(true)}
                  >
                    Place
                  </button>
                  <button
                    className={`rcd-seg-btn${!usePlaceDuration ? " active" : ""}`}
                    onClick={() => setUsePlaceDuration(false)}
                  >
                    Schedule
                  </button>
                </div>
              </div>

              <div className="rcd-row">
                <span className="rcd-label">Pace</span>
                <select
                  className="rcd-hour-select"
                  value={paceMultiplier}
                  onChange={(e) => setPaceMultiplier(e.target.value)}
                >
                  {PACE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

            </div>

            <div className="rcd-footer">
              <button className="rcd-btn rcd-btn-cancel" onClick={onHide}>Cancel</button>
              <button
                className="rcd-btn rcd-btn-action"
                onClick={() => { onConfirm({ startHour, endHour, usePlaceDuration, avoidClosed, geography: reorderByLocation, paceMultiplier: Number(paceMultiplier) || 1 }); onHide(); }}
              >
                Run
              </button>
            </div>

          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RecalculateDialog;
